import { MESSAGE_MEDIUM_CONTENT_MAPPER } from 'modules/campaigns/lib/utils';
import { CampaignTypeFilter, MessageMedium } from 'modules/domains/campaign';
import {
  DEFAULT_THRESHOLD_FORM_VALUE,
  METRICS_TO_DISPLAY_NAMES,
  MetricName,
  ThresholdType,
} from '../../constants';
import {
  AlertMetricsByMedium,
  AlertsFormState,
  DirtyFields,
  ParsedKey,
  ThresholdInputValue,
} from '../../types';
import { getMetricData, getTypeKey, parseAlertKey } from '../../utils';

type AlertSummary = string;
type UpdatedAlertsSummaryList = {
  Enabled: AlertSummary[];
  Disabled: AlertSummary[];
};

const singularize = (val: string, text: string) => (Number(val) === 1 ? text.slice(0, -1) : text);

const getMessageMediumForSummary = (messageMedium: MessageMedium) => {
  const { displayName } = MESSAGE_MEDIUM_CONTENT_MAPPER[messageMedium];
  return displayName;
};

export const getThresholdForSummary = (threshold: ThresholdInputValue, typeKey: ThresholdType) =>
  `${threshold}${typeKey === ThresholdType.Percentage ? '%' : ''}` as const;

const getMetricForSummary = (
  metricName: MetricName,
  threshold: ThresholdInputValue | undefined,
  typeKey: ThresholdType,
) => {
  const metricDisplayName = METRICS_TO_DISPLAY_NAMES[metricName].toLocaleLowerCase();

  return typeKey === ThresholdType.Number
    ? singularize(threshold || DEFAULT_THRESHOLD_FORM_VALUE, metricDisplayName)
    : metricDisplayName;
};

export const useUpdatedAlertsSummary = (
  allFormValues: AlertsFormState,
  {
    allAlertMetrics,
    dirtyFields,
  }: {
    allAlertMetrics: AlertMetricsByMedium;
    dirtyFields: Record<string, DirtyFields>;
  },
) =>
  Object.entries(dirtyFields).reduce(
    (acc, curr) => {
      const [key] = curr;

      const parsedKey = parseAlertKey(key) as ParsedKey;
      const [messageMedium, campaignType, metricName] = parsedKey;

      const valuesForCurrAlert = allFormValues[key];
      const { frequency, status } = valuesForCurrAlert;
      const metricMetadata = getMetricData({
        allAlertMetrics,
        messageMedium,
        metricName,
      });
      const typeKey = getTypeKey(metricMetadata?.valueType);
      const threshold = valuesForCurrAlert[typeKey];
      const medium = getMessageMediumForSummary(messageMedium as MessageMedium);
      const frequencyText =
        campaignType === CampaignTypeFilter.Triggered ? `every ${frequency}` : frequency;

      const thresholdText = getThresholdForSummary(threshold as ThresholdInputValue, typeKey);
      const metricText = getMetricForSummary(metricName, threshold, typeKey);
      const timeText = singularize(frequency, 'hours');

      const entry = `${medium}, ${campaignType}, ${thresholdText} ${metricText} after ${frequencyText} ${timeText}`;

      return {
        ...acc,
        ...(status ? { Enabled: [...acc.Enabled, entry] } : { Disabled: [...acc.Disabled, entry] }),
      };
    },
    { Enabled: [], Disabled: [] } as UpdatedAlertsSummaryList,
  );
